import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { adminAPI } from '../../api/client'
import type { AdminEarningsSummary } from './adminTypes'
import { exportCsv } from './adminUtils'

type Props = {
  token: string
}

export default function AdminEarningsPanel({ token }: Props) {
  const { t } = useTranslation()
  const [data, setData] = useState<AdminEarningsSummary | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const load = useCallback(() => {
    setLoading(true)
    setError('')
    adminAPI
      .earningsSummary(token)
      .then((res) => setData(res.data as AdminEarningsSummary))
      .catch((err) => setError(err.response?.data?.detail || err.message))
      .finally(() => setLoading(false))
  }, [token])

  useEffect(() => {
    load()
  }, [load])

  return (
    <div className="vc-admin-panel vc-admin-panel--card">
      <header className="vc-admin-panel__head">
        <div>
          <h2>{t('admin.nav.earnings')}</h2>
          <p className="vc-admin-panel__hint">{t('admin.earningsIntro')}</p>
        </div>
        <div className="vc-admin-panel__actions">
          {data ? (
            <button
              type="button"
              className="vc-admin-btn vc-admin-btn--ghost"
              onClick={() =>
                exportCsv(
                  'top-sellers.csv',
                  ['email', 'earnings', 'deals'],
                  data.top_sellers.map((row) => [
                    row.email ?? '',
                    row.earnings_eur,
                    row.deals_count,
                  ]),
                )
              }
            >
              {t('admin.exportCsv')}
            </button>
          ) : null}
          <button type="button" className="vc-admin-btn vc-admin-btn--ghost" onClick={load}>
            {loading ? '…' : t('catalog.refresh')}
          </button>
        </div>
      </header>
      {error ? <p className="vc-admin-error">{error}</p> : null}
      {data ? (
        <>
          <div className="vc-admin-stats vc-admin-stats--3">
            <div className="vc-admin-stat">
              <span className="vc-admin-stat__value">€{data.platform_fees_eur.toFixed(2)}</span>
              <span className="vc-admin-stat__label">{t('admin.platformFees')}</span>
            </div>
            <div className="vc-admin-stat">
              <span className="vc-admin-stat__value">€{data.seller_payouts_eur.toFixed(2)}</span>
              <span className="vc-admin-stat__label">{t('admin.sellerPayouts')}</span>
            </div>
            <div className="vc-admin-stat">
              <span className="vc-admin-stat__value">{data.completed_transactions}</span>
              <span className="vc-admin-stat__label">{t('admin.completedDeals')}</span>
            </div>
          </div>
          <section className="vc-admin-card">
            <h3>{t('admin.topSellers')}</h3>
            {data.top_sellers.length === 0 ? (
              <p className="meta-text">{t('admin.noUsers')}</p>
            ) : (
              <div className="vc-admin-table-wrap">
                <table className="vc-admin-table">
                  <thead>
                    <tr>
                      <th>{t('auth.email')}</th>
                      <th>{t('admin.sellerEarnings')}</th>
                      <th>{t('admin.completedDeals')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.top_sellers.map((row) => (
                      <tr key={row.user_id}>
                        <td>
                          <strong>{row.full_name || row.email || `#${row.user_id}`}</strong>
                          {row.full_name && row.email ? (
                            <div className="vc-admin-muted">{row.email}</div>
                          ) : null}
                        </td>
                        <td>€{row.earnings_eur.toFixed(2)}</td>
                        <td>{row.deals_count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      ) : loading ? (
        <p className="meta-text">{t('common.loading')}</p>
      ) : null}
    </div>
  )
}
